import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
    CrearPresentacionDTO,
    getPdfRarResponse,
    presentacionDetalle,
  } from '../pages/Rar/types/types';


const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});


const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

/**
 * Arma el multipart de la carga masiva de RAR.
 *
 * Está afuera del endpoint y exportado para poder verificar el contrato en un test:
 * MSW v1 no puede leer un body `FormData` bajo jsdom, así que un test de red no llega a
 * inspeccionar lo que se manda.
 *
 * El `idEstablecimiento` viaja en el form y no en el Excel (D35): lo aporta el contexto
 * de la pantalla, así que el operador no lo tipea. En un multipart todo llega como
 * string; el `@Type(() => Number)` del DTO del backend lo convierte.
 */
export const construirFormDataCargaMasivaRar = (
    archivo: File,
    idEstablecimiento: number
): FormData => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('idEstablecimiento', String(idEstablecimiento));
    return formData;
};

/** Ruta del endpoint de carga masiva, compartida con su test de contrato. */
export const URL_CARGAR_MASIVO_RAR = 'presentaciones-rar/cargar-masivo';

/**
 * Rutas del flujo de HU-2.1.3 a HU-2.1.7.
 *
 * Exportadas como constantes para poder verificarlas en un test de contrato: un typo en
 * una URL es un 404 que ningún otro test detecta —el mock de MSW tampoco, porque se
 * registra con la misma constante equivocada—.
 *
 * ⚠️ Todas las de lote van bajo `lotes/:idLote` y NO bajo `:idLote`. En el controller del
 * backend `:idPresentacion` ya ocupa esa posición del path, y dos identificadores
 * enteros distintos en el mismo lugar es un 404 —o algo peor— imposible de detectar.
 */
export const URLS_LOTE_RAR = {
    validar: (idLote: number) => `presentaciones-rar/lotes/${idLote}/validar`,
    progreso: (idLote: number) => `presentaciones-rar/lotes/${idLote}/progreso`,
    detalles: (idLote: number) => `presentaciones-rar/lotes/${idLote}/detalles`,
    reporte: (idLote: number) => `presentaciones-rar/lotes/${idLote}/reporte`,
    filasConError: (idLote: number) =>
        `presentaciones-rar/lotes/${idLote}/filas-con-error`,
    procesar: (idLote: number) => `presentaciones-rar/lotes/${idLote}/procesar`,
    reprocesarFallidos: (idLote: number) =>
        `presentaciones-rar/lotes/${idLote}/reprocesar-fallidos`,
    historico: 'presentaciones-rar/lotes',
    /**
     * De qué camino vino una presentación (CR-03, punto 2).
     *
     * `presentaciones` no tiene columna de origen: el backend lo infiere de la
     * existencia de un lote masivo asociado.
     */
    origen: (idPresentacion: number) =>
        `presentaciones-rar/${idPresentacion}/origen`,
    /**
     * Identifica por CUIL y no por el id del snapshot: ese id vive en `empleados`, una
     * tabla que ninguna vista del Front consulta —la tabla de la pantalla lista la
     * nómina del ESTABLECIMIENTO—. El CUIL es el identificador estable de la persona.
     */
    quitarPersona: (idPresentacion: number, cuil: string) =>
        `presentaciones-rar/${idPresentacion}/empleados/${cuil}`
} as const;

/**
 * Estados del lote, espejando el ENUM del backend.
 *
 * Se declaran acá y no se derivan de un string libre para que el hook pueda decidir si
 * el polling sigue activo sin comparar literales sueltos por toda la UI.
 */
export const ESTADO_LOTE_RAR = {
    ARCHIVO_CARGADO: 'ARCHIVO_CARGADO',
    VALIDACION_EN_PROCESO: 'VALIDACION_EN_PROCESO',
    VALIDACION_COMPLETADA: 'VALIDACION_COMPLETADA',
    PROCESAMIENTO_EN_PROCESO: 'PROCESAMIENTO_EN_PROCESO',
    PROCESAMIENTO_COMPLETADO: 'PROCESAMIENTO_COMPLETADO',
    ERROR: 'ERROR'
} as const;

export type EstadoLoteRar = (typeof ESTADO_LOTE_RAR)[keyof typeof ESTADO_LOTE_RAR];

/**
 * Los estados en los que el lote está TRABAJANDO.
 *
 * Es lo que enciende el polling. Los terminales lo apagan: sin eso, una pestaña olvidada
 * consulta el progreso para siempre.
 */
export const ESTADOS_LOTE_RAR_EN_CURSO: Array<EstadoLoteRar> = [
    ESTADO_LOTE_RAR.VALIDACION_EN_PROCESO,
    ESTADO_LOTE_RAR.PROCESAMIENTO_EN_PROCESO
];

export const presentacionesRarApi = createApi({
    reducerPath: 'presentacionRar',
    tagTypes: [
        'presentacionesRar',
        'PresentacionRarById',
        'Responsables',
        'HistoricoLotesRar',
        'LoteRarEnCurso'
    ],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getDetalleNominaRar: builder.query<
            presentacionDetalle,
            { id_presentacion: number; take: number; skip: number, cuil?: string, expuesto?: boolean }
        >({
            query: ({ id_presentacion, take, skip, cuil, expuesto }) => {
                const params: any = { take, skip };
                if (cuil) params.cuil = cuil;
                if (expuesto) params.expuesto = expuesto;
            
                return {
                  url: `presentaciones-rar/${id_presentacion}/nomina`,
                  params, 
                };
              },
            // 🔴 Sin esto, quitar una persona no refrescaba la grilla.
            //
            // `quitarPersonaBorradorRar` invalida `PresentacionRarById` desde que se
            // escribió, pero el tag estaba declarado en `tagTypes` y NINGUNA query lo
            // proveía: invalidar un tag que nadie provee no hace nada. La fila quitada
            // seguía en pantalla con la caché vieja, y el segundo click sí llegaba al
            // backend y fallaba con "el empleado no está en el borrador".
            providesTags: ['PresentacionRarById']
        }),
        createPresentacionRar: builder.mutation({
            query: (formData) => {
                return {
                    url: '/presentaciones-rar',
                    method: 'POST',
                    body: formData
                };
            }
        }),
        createPresentacionRarV2: builder.mutation<void, any>({
            query: (formData) => {
                return {
                    url: 'presentaciones-rar/rar-recibido',
                    method: 'POST',
                    body: formData
                };
            }
        }),
        /**
         * Presenta EL BORRADOR de la carga masiva.
         *
         * No lleva id de presentación: el backend lo resuelve desde el
         * establecimiento. Es otro endpoint que el de `enviar-rar` a propósito —aquel
         * arma una presentación nueva desde la nómina del establecimiento, este
         * presenta lo que se cargó—, y `enviar-rar` lo comparte el circuito individual.
         */
        presentarBorradorRar: builder.mutation<
            void,
            { idEstablecimiento: number; responsables: Array<unknown> }
        >({
            query: (body) => ({
                url: 'presentaciones-rar/lotes/presentar-borrador',
                method: 'POST',
                body
            }),
            invalidatesTags: ['HistoricoLotesRar', 'PresentacionRarById']
        }),

        presentarRar: builder.mutation({
            query: ({ idPresentacion }) => ({
                url: `presentaciones-rar/${idPresentacion}/presentar`,
                method: 'POST'
            }),
            invalidatesTags: ['PresentacionRarById']
        }),
        getPresentacionRarPDFbyId: builder.query<getPdfRarResponse, { idPresentacion: number, typeId: number }>({
            query: ({ idPresentacion, typeId }) => ({
                url: `presentaciones/${idPresentacion}/archivo`,
                params: { idTipo: 2 }
            })
        }),
        getResponsablesByPresentacionId: builder.query<any, { idPresentacion: number }>({
            query: ({ idPresentacion }) => ({
                url: `presentaciones/${idPresentacion}/responsables`,
                method: 'GET',
            }),
            providesTags: ['Responsables']
        }),

        /**
         * Descargas de la carga masiva de RAR (HU-2.1.1).
         *
         * Piden el binario directo con `response.blob()`. Eso DEPENDE de dos cosas del
         * backend, que viajan en el PR de Back-APIRar:
         *
         *  1. que el controller escriba el buffer con `@Res()` en vez de devolverlo
         *     (si lo devuelve, NestJS lo serializa a {"type":"Buffer","data":[...]}), y
         *  2. que `lambda.mts` declare `binarySettings` con el MIME de xlsx (sin eso
         *     API Gateway lo manda como texto y el archivo llega corrupto).
         *
         * Contra un backend sin esos dos arreglos, `blob()` devuelve un Blob con el
         * texto del JSON adentro y se descarga un .xlsx ilegible. Los dos PRs se
         * despliegan juntos, backend primero.
         *
         * ⚠️ NO confundir con la plantilla del bloque "Importar Nómina RAR", que es un
         * archivo estático del Front de 8 columnas y alimenta el upload vigente. Esta
         * es la plantilla NUEVA de 9 columnas de la carga masiva; conviven a propósito
         * hasta que se migre el upload.
         */
        downloadPlantillaRar: builder.query<Blob, void>({
            query: () => ({
                url: 'presentaciones-rar/plantilla',
                method: 'GET',
                responseHandler: (response) => response.blob()
            })
        }),
        /**
         * Los catálogos del ESTABLECIMIENTO, no los del operador.
         *
         * `idEstablecimiento` es obligatorio: el backend resuelve con él el organismo, y
         * ese es el mismo alcance que usa la validación del lote. Sin el parámetro
         * responde 400 — a propósito, porque caer a "todos los organismos del operador"
         * devolvería puestos que después se rechazan.
         */
        downloadCatalogosRar: builder.query<Blob, { idEstablecimiento: number }>({
            query: ({ idEstablecimiento }) => ({
                url: 'presentaciones-rar/catalogos-excel',
                method: 'GET',
                params: { idEstablecimiento },
                responseHandler: (response) => response.blob()
            })
        }),

        /**
         * Sube el Excel de carga masiva de RAR (HU-2.1.2).
         *
         * No se setea `Content-Type` a mano: el navegador tiene que generar el boundary
         * del multipart. Si se pisa, multer no parsea el body y el backend recibe el
         * archivo vacío.
         *
         * El `idEstablecimiento` viaja en el form y no en el Excel (D35): lo aporta el
         * contexto de la pantalla, así que el operador no lo tipea.
         */
        cargarMasivoRar: builder.mutation<
            CargarMasivoRarResponse,
            { archivo: File; idEstablecimiento: number }
        >({
            query: ({ archivo, idEstablecimiento }) => ({
                url: URL_CARGAR_MASIVO_RAR,
                method: 'POST',
                body: construirFormDataCargaMasivaRar(archivo, idEstablecimiento)
            })
        }),

        // ── HU-2.1.3 · validación de filas y resultados ──────────────────────

        /**
         * Dispara la validación del lote. Responde 202: cuando vuelve, las filas están
         * encoladas y sin validar.
         */
        validarLoteRar: builder.mutation<ValidarLoteRarResponse, { idLote: number }>({
            query: ({ idLote }) => ({
                url: URLS_LOTE_RAR.validar(idLote),
                method: 'POST'
            }),
            // También el histórico: sin esto, la grilla de cargas anteriores se queda en
            // «Archivo cargado» mientras la validación corre. Que el lote TERMINE lo
            // refresca el hook, que es el único que ve la transición.
            invalidatesTags: ['LoteRarEnCurso', 'HistoricoLotesRar']
        }),

        /**
         * Estado y contadores del lote. Es el endpoint del polling.
         *
         * El `pollingInterval` lo decide el HOOK, no este endpoint: depende del estado
         * del lote, que solo el hook conoce.
         */
        getProgresoLoteRar: builder.query<ProgresoLoteRarResponse, { idLote: number }>({
            query: ({ idLote }) => ({ url: URLS_LOTE_RAR.progreso(idLote) }),
            /**
             * Sin este tag, el polling NO arranca.
             *
             * El intervalo lo decide el hook a partir del estado del lote. Después de
             * disparar Validar o Procesar, la caché todavía tiene el estado ANTERIOR
             * —ARCHIVO_CARGADO o VALIDACION_COMPLETADA—, que no está en
             * ESTADOS_LOTE_RAR_EN_CURSO: el intervalo queda en 0 y la pantalla se
             * congela hasta que el operador refresca a mano.
             *
             * Las tres mutaciones invalidan este tag, así que al volver el 202 se
             * relee el progreso, el estado pasa a EN_PROCESO y recién ahí el polling
             * se enciende solo.
             */
            providesTags: ['LoteRarEnCurso']
        }),

        /** Las filas del lote, filtradas y paginadas. */
        getDetallesLoteRar: builder.query<DetallesLoteRarResponse, DetallesLoteRarParams>(
            {
                query: ({ idLote, ...params }) => ({
                    url: URLS_LOTE_RAR.detalles(idLote),
                    // Los `undefined` no se mandan: RTK Query los omite del query
                    // string, así el default del DTO del backend (validacion =
                    // RECHAZADA) sigue aplicando cuando el filtro no se tocó.
                    params
                }),
                // Mismo motivo que el progreso: la tabla tiene que repintarse cuando
                // la validación o el procesamiento arrancan, sin depender de un F5.
                providesTags: ['LoteRarEnCurso']
            }
        ),

        // ── HU-2.1.4 · reportes descargables ────────────────────────────────

        /**
         * Los dos reportes piden el binario directo con `response.blob()`, igual que la
         * plantilla y los catálogos de HU-2.1.1: el backend los escribe con `@Res()` y
         * `lambda.mts` declara el MIME de xlsx en `binarySettings`.
         */
        downloadReporteLoteRar: builder.query<Blob, { idLote: number }>({
            query: ({ idLote }) => ({
                url: URLS_LOTE_RAR.reporte(idLote),
                method: 'GET',
                responseHandler: (response) => response.blob()
            })
        }),
        downloadFilasConErrorRar: builder.query<Blob, { idLote: number }>({
            query: ({ idLote }) => ({
                url: URLS_LOTE_RAR.filasConError(idLote),
                method: 'GET',
                responseHandler: (response) => response.blob()
            })
        }),

        // ── HU-2.1.5 · procesamiento ────────────────────────────────────────

        /**
         * Aplica las filas válidas. Responde 202.
         *
         * El body `{confirmar: true}` es obligatorio y lo exige el backend: es lo que
         * garantiza que el operador vio el diálogo con las consecuencias antes de
         * disparar cambios irreversibles sobre el borrador (D33).
         */
        procesarLoteRar: builder.mutation<ProcesarLoteRarResponse, { idLote: number }>({
            query: ({ idLote }) => ({
                url: URLS_LOTE_RAR.procesar(idLote),
                method: 'POST',
                body: { confirmar: true }
            }),
            // Además del progreso invalida el histórico: el contador de empleados del
            // borrador cambia con las filas aplicadas.
            invalidatesTags: ['LoteRarEnCurso', 'HistoricoLotesRar']
        }),

        // ── HU-2.1.6 · reproceso ────────────────────────────────────────────

        reprocesarFallidosRar: builder.mutation<
            ReprocesarFallidosRarResponse,
            { idLote: number }
        >({
            query: ({ idLote }) => ({
                url: URLS_LOTE_RAR.reprocesarFallidos(idLote),
                method: 'POST'
            }),
            invalidatesTags: ['LoteRarEnCurso', 'HistoricoLotesRar']
        }),

        // ── HU-2.1.7 · histórico y quitar persona ───────────────────────────

        /** Histórico del establecimiento + estado del borrador, en una sola respuesta. */
        getHistoricoLotesRar: builder.query<
            HistoricoLotesRarResponse,
            HistoricoLotesRarParams
        >({
            query: (params) => ({ url: URLS_LOTE_RAR.historico, params }),
            providesTags: ['HistoricoLotesRar']
        }),

        /**
         * Si la presentación tiene lotes de carga masiva.
         *
         * Se consulta al hacer click en el ojo de la solapa Borradores, no al montar la
         * grilla: una fila por presentación serían N llamadas para una decisión que solo
         * hace falta cuando el operador efectivamente abre una.
         *
         * Sin `providesTags`: el origen de una presentación no cambia, y cachearlo por
         * tag obligaría a invalidarlo desde las mutaciones del flujo masivo sin ninguna
         * ganancia.
         */
        getOrigenPresentacionRar: builder.query<
            { tieneCargaMasiva: boolean },
            number
        >({
            query: (idPresentacion) => ({
                url: URLS_LOTE_RAR.origen(idPresentacion)
            })
        }),

        /**
         * Quita a una persona del borrador (D39).
         *
         * Invalida el histórico para que el contador de "empleados cargados" del bloque
         * de estado del borrador se actualice sin que la pantalla lo recalcule a mano.
         */
        quitarPersonaBorradorRar: builder.mutation<
            { quitado: boolean },
            { idPresentacion: number; cuil: string }
        >({
            query: ({ idPresentacion, cuil }) => ({
                url: URLS_LOTE_RAR.quitarPersona(idPresentacion, cuil),
                method: 'DELETE'
            }),
            invalidatesTags: ['HistoricoLotesRar', 'PresentacionRarById']
        })
    })
});

export type CargarMasivoRarResponse = {
    loteId: number;
    idPresentacion: number;
    totalFilas: number;
    estado: string;
};

export type ValidarLoteRarResponse = {
    loteId: number;
    estado: EstadoLoteRar;
    totalFilas: number;
    /** Menor que totalFilas si hubo CUIL duplicados: esas se rechazaron en el acto. */
    filasEncoladas: number;
};

export type ProgresoLoteRarResponse = {
    loteId: number;
    nombreArchivo: string | null;
    fechaAlta: string;
    estado: EstadoLoteRar;
    /** El motivo del rechazo global, si el lote quedó en ERROR. */
    motivoError: string | null;
    resumen: {
        totalFilas: number;
        totalValidados: number;
        totalFallidos: number;
        totalPendientes: number;
        porcentaje: number;
    };
    /** Por código de error: es lo que alimenta los chips clickeables. */
    erroresAgrupados: Record<string, number>;
    /**
     * Por código de advertencia (CR-02): SIN_ESTABLECIMIENTO y OTRO_ESTABLECIMIENTO.
     * Es lo que desglosa el modal de confirmación.
     */
    advertenciasAgrupadas: Record<string, number>;
    /** Filas válidas con advertencia. La suma de las anteriores. */
    totalConAdvertencia: number;
    /** Lo resuelve el BACKEND: la regla no se replica acá. */
    puedeProcesar: boolean;
    puedeReprocesar: boolean;
    procesamiento: {
        /** ⚠️ El denominador de este paso son las filas VÁLIDAS, no las del archivo. */
        totalAProcesar: number;
        totalProcesadas: number;
        totalPendientes: number;
        porcentaje: number;
        simpOk: number;
        simpError: number;
        puestoOk: number;
        puestoError: number;
        sectorOk: number;
        sectorError: number;
        asignacionOk: number;
        asignacionError: number;
        /** Fallidas que todavía se pueden reintentar. */
        reintentables: number;
        /** Fallidas que agotaron los intentos: el reproceso las ignora. */
        agotadas: number;
    };
};

export type EstadoEtapaRar = 'PENDIENTE' | 'OK' | 'ERROR';
export type EstadoValidacionFilaRar = 'PENDIENTE' | 'VALIDA' | 'RECHAZADA';

/**
 * Advertencias de asignación (CR-02).
 *
 * NO rechazan la fila: avisan que al procesar el empleado va a quedar asignado a
 * este establecimiento. Por eso se muestran sin el color de error.
 */
export const CODIGO_ADVERTENCIA_RAR = {
    SIN_ESTABLECIMIENTO: 'SIN_ESTABLECIMIENTO',
    OTRO_ESTABLECIMIENTO: 'OTRO_ESTABLECIMIENTO'
} as const;

export type CodigoAdvertenciaRar =
    (typeof CODIGO_ADVERTENCIA_RAR)[keyof typeof CODIGO_ADVERTENCIA_RAR];

export type DetalleLoteRarItem = {
    idDetalle: number;
    numeroFila: number;
    cuil: string | null;
    nombre: string | null;
    apellido: string | null;
    puesto: string | null;
    sector: string | null;
    estadoValidacion: EstadoValidacionFilaRar;
    codigoError: string | null;
    /** El texto que ve el operador. Lo redacta el backend. */
    errorValidacion: string | null;
    /** CR-02. `null` si la fila no tiene advertencia de asignación. */
    codigoAdvertencia: CodigoAdvertenciaRar | null;
    /** El texto de la advertencia. También lo redacta el backend. */
    advertenciaValidacion: string | null;
    estadoSimp: EstadoEtapaRar;
    estadoPuesto: EstadoEtapaRar;
    estadoSector: EstadoEtapaRar;
    estadoAsignacion: EstadoEtapaRar;
    errorEjecucion: string | null;
    /** La fila agotó los intentos: el reproceso ya no la va a tomar. */
    sinReintentos: boolean;
    /**
     * El nombre del Excel no coincide con el de la nómina.
     *
     * `nombre` y `apellido` SIEMPRE vienen de la nómina: el Excel es orientativo. Esto
     * es solo para avisarle al operador que lo que escribió no es lo que ve.
     *
     * Opcional: contra un backend anterior el campo no viene y no se muestra nada.
     */
    nombreDifiereDelExcel?: boolean;
    /** Lo que decía el Excel. Solo cuando difiere, para el tooltip. */
    nombreEnElExcel?: string | null;
};

export type PaginacionRar = {
    pagina: number;
    total: number;
    totalPaginas: number;
    limite: number;
};

export type DetallesLoteRarResponse = {
    detalles: Array<DetalleLoteRarItem>;
    paginacion: PaginacionRar;
};

export type DetallesLoteRarParams = {
    idLote: number;
    validacion?: EstadoValidacionFilaRar | 'ALL';
    ejecucion?: EstadoEtapaRar | 'ALL';
    codigoError?: string;
    busqueda?: string;
    pagina?: number;
    limite?: number;
};

export type ProcesarLoteRarResponse = {
    loteId: number;
    estado: EstadoLoteRar;
    registrosAProcesar: number;
};

export type ReprocesarFallidosRarResponse = {
    loteId: number;
    estado: EstadoLoteRar;
    filasReencoladas: number;
};

export type LoteHistoricoRar = {
    loteId: number;
    nombreArchivo: string | null;
    fechaAlta: string;
    subidoPor: string;
    estado: EstadoLoteRar;
    /** Presente en los lotes rechazados por error global (D42). */
    motivoError: string | null;
    idPresentacion: number | null;
    totalFilas: number;
    totalValidados: number;
    totalFallidos: number;
    totalEjecutadosOk: number;
    totalEjecutadosError: number;
};

export type EstadoBorradorRar = {
    idPresentacion: number;
    empleadosCargados: number;
    fechaUltimaCarga: string | null;
    /** Con un lote en curso, quitar persona no está disponible (REQ-046). */
    hayLoteEnCurso: boolean;
};

/**
 * Los tres conteos del panel de orientación (CR-02).
 *
 * ⚠️ Son hechos, NO una resta: el módulo no informa cuántos empleados faltan
 * declarar (REQ-045), porque ese número no es calculable.
 */
export type OrientacionRar = {
    sinEstablecimiento: number;
    asignadosAEsteEstablecimiento: number;
    /** `null` si el establecimiento no tiene un RAR anterior: el ítem no se muestra. */
    declaradosEnUltimoRar: number | null;
};

export type HistoricoLotesRarResponse = {
    /** `null` si el establecimiento no tiene borrador vigente. */
    borrador: EstadoBorradorRar | null;
    /** `null` si el establecimiento no tiene organismo: el panel no se muestra. */
    orientacion: OrientacionRar | null;
    lotes: Array<LoteHistoricoRar>;
    paginacion: PaginacionRar;
};

export type HistoricoLotesRarParams = {
    idEstablecimiento: number;
    pagina?: number;
    limite?: number;
};

export const {
    useGetDetalleNominaRarQuery,
    useLazyGetDetalleNominaRarQuery,
    useCreatePresentacionRarMutation,
    useCreatePresentacionRarV2Mutation,
    usePresentarRarMutation,
    useGetPresentacionRarPDFbyIdQuery,
    useGetResponsablesByPresentacionIdQuery,
    useLazyGetPresentacionRarPDFbyIdQuery,
    useLazyDownloadPlantillaRarQuery,
    useLazyDownloadCatalogosRarQuery,
    useCargarMasivoRarMutation,
    // HU-2.1.3
    useValidarLoteRarMutation,
    usePresentarBorradorRarMutation,
    useGetProgresoLoteRarQuery,
    useGetDetallesLoteRarQuery,
    // HU-2.1.4
    useLazyDownloadReporteLoteRarQuery,
    useLazyDownloadFilasConErrorRarQuery,
    // HU-2.1.5
    useProcesarLoteRarMutation,
    // HU-2.1.6
    useReprocesarFallidosRarMutation,
    // HU-2.1.7
    useGetHistoricoLotesRarQuery,
    // CR-03
    useLazyGetOrigenPresentacionRarQuery,
    useQuitarPersonaBorradorRarMutation
} = presentacionesRarApi;